/**
 * Session 7 (item 2, UX-269): a one-shot message queued by `RoleSwitcher`
 * right before it navigates to the target workspace, and consumed by
 * `RoleSwitchAnnouncer` (mounted once in `RootLayout`) once that navigation
 * lands — so a screen reader announces "已切換至主辦方工作區"/"已切換至參賽者
 * 工作區" instead of silently landing on a new page with no spoken feedback
 * that anything happened.
 *
 * `sessionStorage`, not React state, because `RoleSwitcher` and
 * `RoleSwitchAnnouncer` are not in a parent/child relationship that survives
 * the full-page navigation `navigate()` performs (PR-267: Role Switch is
 * pure client-side route navigation, never a state mutation) — the message
 * has to outlive the exact tick where `RoleSwitcher`'s own instance for the
 * *previous* route unmounts. `sessionStorage` also means a hard reload
 * mid-switch never resurrects a stale announcement for an unrelated later
 * navigation, since the read is a single consume-once `getItem` +
 * `removeItem` pair.
 */

const STORAGE_KEY = 'napaicp:role-switch-announcement'

/** Called by `RoleSwitcher` immediately before it navigates to the target workspace. */
export function queueRoleSwitchAnnouncement(message: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, message)
  } catch {
    // `sessionStorage` unavailable (private browsing, storage disabled) —
    // the announcement is an accessibility enhancement, never a hard
    // requirement for the switch itself to work, so this fails silently.
  }
}

/** Called by `RoleSwitchAnnouncer` after every navigation; returns and clears the pending message, if any. */
export function consumeRoleSwitchAnnouncement(): string | null {
  try {
    const message = sessionStorage.getItem(STORAGE_KEY)
    if (message !== null) {
      sessionStorage.removeItem(STORAGE_KEY)
    }
    return message
  } catch {
    return null
  }
}
